import dayjs from "dayjs";
import { isNil, sortBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  type DocScanVolumeByPipelineReportQuery,
  DocumentIngestionPipeline,
  type ReportAggregationPeriod,
  useDocScanVolumeByPipelineReportQuery,
} from "../../../generated/graphql";
import { type DateOption } from "../../date-range-picker";
import { PIPELINE_LABELS } from "../../doc-ingestion/pipeline-filter";

type DocScanVolumeBucket =
  DocScanVolumeByPipelineReportQuery["docScanVolumeByPipelineReport"][0];

export type DocScanVolumeRow = {
  startDate: Date;
  endDate: Date;
  [DocumentIngestionPipeline.Legacy]: number;
  [DocumentIngestionPipeline.DocumentExtraction]: number;
  total: number;
};

const toDate = (scalar: DocScanVolumeBucket["startDate"]): Date =>
  new Date(scalar as string);

export const PIPELINES = [
  DocumentIngestionPipeline.Legacy,
  DocumentIngestionPipeline.DocumentExtraction,
];

const defaultChartRowsPerPage = 8;
const expandedChartRowsPerPage = 90;

export const useDocScanVolumeByPipeline = ({
  dateOption,
  isExpanded,
  period,
}: {
  readonly dateOption: DateOption;
  readonly isExpanded: boolean;
  readonly period: ReportAggregationPeriod;
}) => {
  const [page, setPage] = useState(0);
  const { data, loading } = useDocScanVolumeByPipelineReportQuery({
    variables: {
      docScanVolumeByPipelineInput: {
        startDate: dateOption.startDate,
        endDate: dateOption.endDate,
        period,
      },
    },
  });
  const chartRowsPerPage = isExpanded
    ? expandedChartRowsPerPage
    : defaultChartRowsPerPage;
  const volumeData = useMemo(() => {
    const buckets = data?.docScanVolumeByPipelineReport;
    if (isNil(buckets)) return;
    const rowsByStartDate = new Map<number, DocScanVolumeRow>();
    for (const bucket of buckets) {
      const startDate = toDate(bucket.startDate);
      let row = rowsByStartDate.get(startDate.getTime());
      if (row === undefined) {
        row = {
          startDate,
          endDate: toDate(bucket.endDate),
          [DocumentIngestionPipeline.Legacy]: 0,
          [DocumentIngestionPipeline.DocumentExtraction]: 0,
          total: 0,
        };
        rowsByStartDate.set(startDate.getTime(), row);
      }
      row[bucket.pipeline] += bucket.totalCount;
      row.total += bucket.totalCount;
    }
    return sortBy([...rowsByStartDate.values()], (row) =>
      row.startDate.getTime(),
    );
  }, [data]);
  const chartData = useMemo(() => {
    if (isNil(volumeData)) return {};
    const pageRows = volumeData.slice(
      page * chartRowsPerPage,
      (page + 1) * chartRowsPerPage,
    );
    return {
      labels: pageRows.map(
        (row) =>
          `${dayjs(row.startDate).format("MM/DD")}-${dayjs(row.endDate).format("MM/DD/YY")}`,
      ),
      datasets: PIPELINES.map((pipeline) => ({
        label: PIPELINE_LABELS[pipeline],
        data: pageRows.map((row) => row[pipeline]),
      })),
    };
  }, [chartRowsPerPage, page, volumeData]);

  useEffect(() => {
    setPage(
      Math.max(Math.ceil((volumeData?.length ?? 0) / chartRowsPerPage) - 1, 0),
    );
  }, [chartRowsPerPage, volumeData?.length]);

  return {
    chartData,
    chartRowsPerPage,
    loading,
    page,
    setPage,
    volumeData,
  };
};
